import React, { useState, useEffect, useContext } from 'react';
import { apiService } from '../services/apiService';
import { refreshDataContext } from '../context/AppContext';

function EditEventModal({ isOpen, onClose, link, name, isNew = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshData = useContext(refreshDataContext);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    body: '',
    img: '',
    tileImg: '',
    link: link || '',
    dateTime: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchImages();
      if (!isNew && link) {
        fetchEventData();
      } else if (isNew) {
        setFormData({
          name: '',
          body: '',
          img: '../cdn/tm-logo-bg-black_600x400.jpg',
          tileImg: '../cdn/tm-logo-bg-black_320x420.jpg',
          link: link || '',
          dateTime: new Date().toISOString()
        });
        setLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [isOpen, link, isNew]);

  const fetchImages = async () => {
    try {
      const imageData = await apiService.getImages();
      setImages(imageData);
    } catch (err) {
      console.error("Error fetching images:", err);
      setError("Failed to load images. Please try again.");
    }
  };

  const fetchEventData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getEvent(name, link);
      setFormData({
        name: response.name || '',
        body: response.body || '',
        img: response.img || '../cdn/tm-logo-bg-black_600x400.jpg',
        tileImg: response.tileImg || '../cdn/tm-logo-bg-black_320x420.jpg',
        link: response.link || '',
        dateTime: response.dateTime || new Date().toISOString()
      });
      setLoading(false);
    } catch (err) {
      console.error("Error fetching event data:", err);
      setError("Failed to load event data. Please try again.");
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isNew) {
        await apiService.createEvent(formData.name, formData.link, formData);
      } else {
        await apiService.updateEvent(name, link, formData);
      }

      // Refresh the data in the context
      refreshData();

      setLoading(false);
      onClose(true); // Close modal and indicate success
    } catch (err) {
      console.error(`Error ${isNew ? 'creating' : 'updating'} event:`, err);
      setError(`Failed to ${isNew ? 'create' : 'update'} event. Please try again.`);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      setLoading(true);
      try {
        await apiService.deleteEvent(name, link);

        // Refresh the data in the context
        refreshData();

        setLoading(false);
        onClose(true); // Close modal and indicate success
      } catch (err) {
        console.error("Error deleting event:", err);
        setError("Failed to delete event. Please try again.");
        setLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '5px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div className="modal-header">
          <h3>{isNew ? 'Add New Event' : 'Edit Event'}</h3>
          <button
            type="button"
            className="btn btn-close me-auto"
            onClick={() => onClose(false)}
            aria-label="Close"
          > X </button>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center my-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Event Name</label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            {isNew && (
              <div className="mb-3">
                <label htmlFor="link" className="form-label">URL Link (unique identifier)</label>
                <input
                  type="text"
                  className="form-control"
                  id="link"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  required
                  placeholder="e.g., hiphop-workshop-nov-2024"
                />
                <small className="form-text text-muted">
                  This will be used in the URL and cannot be changed later.
                </small>
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="dateTime" className="form-label">Event Date and Time</label>
              <input
                type="datetime-local"
                className="form-control"
                id="dateTime"
                name="dateTime"
                value={formData.dateTime.slice(0, 16)} // Format for datetime-local input
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="body" className="form-label">Description</label>
              <textarea
                className="form-control"
                id="body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                required
                rows={10}
                placeholder="Enter event description. Use newlines (\n) to separate paragraphs."
              />
              <small className="form-text text-muted">
                Use newlines to separate paragraphs. Each newline will create a new paragraph in the display.
              </small>
            </div>

            <div className="mb-3">
              <label htmlFor="img" className="form-label">Main Image</label>
              <select
                className="form-control"
                id="img"
                name="img"
                value={formData.img}
                onChange={handleChange}
                required
              >
                <option value="">Select an image</option>
                {images.map((image) => (
                  image.variants.map((variant) => {
                    if (variant.dimensions === "600x400") {
                      return (
                        <option key={variant.path} value={`/${variant.path}`}>
                          {image.name} (600x400)
                        </option>
                      );
                    }
                    return null;
                  })
                ))}
              </select>
              <small className="form-text text-muted">
                Select the main image (600x400 pixels). <a href="/admin/images" target="_blank" rel="noopener noreferrer">Manage Images</a>
              </small>
            </div>

            <div className="mb-3">
              <label htmlFor="tileImg" className="form-label">Tile Image</label>
              <select
                className="form-control"
                id="tileImg"
                name="tileImg"
                value={formData.tileImg}
                onChange={handleChange}
                required
              >
                <option value="">Select an image</option>
                {images.map((image) => (
                  image.variants.map((variant) => {
                    if (variant.dimensions === "320x420") {
                      return (
                        <option key={variant.path} value={`/${variant.path}`}>
                          {image.name} (320x420)
                        </option>
                      );
                    }
                    return null;
                  })
                ))}
              </select>
              <small className="form-text text-muted">
                Select the tile image (320x420 pixels). <a href="/admin/images" target="_blank" rel="noopener noreferrer">Manage Images</a>
              </small>
            </div>

            <div className="modal-footer">
              {!isNew && (
                <button
                  type="button"
                  className="btn btn-danger me-auto"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => onClose(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isNew ? 'Create' : 'Save Changes')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default EditEventModal;